import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { GuestSelectors, UserSelectors } from '@recruitee/account-data';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
} from '@recruitee/ngrx';
import type { Note, NotePayload } from '@recruitee/notes-types';

export const candidateScope = (id: number) => `candidate_${id}`;
export const offerScope = (id: number) => `offer_${id}`;
export const talentPoolScope = (id: number) => `talent_pool_${id}`;
export const requisitionScope = (id: number) => `requisition_${id}`;

const {
  actions,
  selectors: defaultSelectors,
  namespace,
  reducer,
} = createEntityCollectionStore<Note>()('notes', {
  references: makeRef => [
    makeRef('adminId', 'admin', UserSelectors.byId),
    makeRef('guestId', 'guest', GuestSelectors.byId),
  ],
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const actions = getEntityCollectionActions(adapter, ({ id }) => id!);

    return {
      fetchCandidateNotes: (state, payload: { candidateId: number }) =>
        actions.fetchCollection(state, { scope: candidateScope(payload.candidateId) }),
      fetchCandidateNotesSuccess: actions.fetchCollectionSuccess,
      fetchCandidateNotesFailed: actions.fetchCollectionFailed,

      createCandidateNote: (
        state,
        payload: {
          note: Note;
          attachmentIds?: number[];
          candidateId: number;
          correlationId: string;
        },
      ) =>
        actions.createOnePessimistic(state, {
          data: payload.note,
          scope: candidateScope(payload.candidateId),
          correlationId: payload.correlationId,
        }),
      createCandidateNoteSuccess: actions.createOnePessimisticSuccess,
      createCandidateNoteFailed: actions.createOnePessimisticFailed,

      fetchOfferNotes: (state, payload: { offerId: number }) =>
        actions.fetchCollection(state, { scope: offerScope(payload.offerId) }),
      fetchOfferNotesSuccess: actions.fetchCollectionSuccess,
      fetchOfferNotesFailed: actions.fetchCollectionFailed,

      fetchTalentPoolNotes: (state, payload: { offerId: number }) =>
        actions.fetchCollection(state, { scope: talentPoolScope(payload.offerId) }),
      fetchTalentPoolNotesSuccess: actions.fetchCollectionSuccess,
      fetchTalentPoolNotesFailed: actions.fetchCollectionFailed,

      fetchRequisitionNotes: (state, payload: { requisitionId: number }) =>
        actions.fetchCollection(state, { scope: requisitionScope(payload.requisitionId) }),
      fetchRequisitionNotesSuccess: actions.fetchCollectionSuccess,
      fetchRequisitionNotesFailed: actions.fetchCollectionFailed,

      updateOnePessimistic: (state, payload: { data: Note; attachmentIds?: number[] }) =>
        actions.updateOnePessimistic(state, payload),

      updateChecklist: (state, payload: { id: number; bodyJson: object }) =>
        adapter.markAsPending(state, { entities: [payload.id], scope: null }),
      updateChecklistSuccess: actions.updateOnePessimisticSuccess,
      updateChecklistFailed: actions.updateOnePessimisticFailed,

      createOfferNote: (
        state,
        payload: {
          note: NotePayload;
          attachmentIds?: number[];
          offerId: number;
          correlationId: string;
        },
      ) =>
        actions.createOnePessimistic(state, {
          data: payload.note,
          scope: offerScope(payload.offerId),
          correlationId: payload.correlationId,
        }),
      createOfferNoteSuccess: actions.createOnePessimisticSuccess,
      createOfferNoteFailed: actions.createOnePessimisticFailed,

      createTalentPoolNote: (
        state,
        payload: {
          note: NotePayload;
          attachmentIds?: number[];
          offerId: number;
          correlationId: string;
        },
      ) =>
        actions.createOnePessimistic(state, {
          data: payload.note,
          scope: talentPoolScope(payload.offerId),
          correlationId: payload.correlationId,
        }),
      createTalentPoolNoteSuccess: actions.createOnePessimisticSuccess,
      createTalentPoolNoteFailed: actions.createOnePessimisticFailed,

      createRequisitionNote: (
        state,
        payload: {
          note: NotePayload;
          attachmentIds?: number[];
          requisitionId: number;
          correlationId: string;
        },
      ) =>
        actions.createOnePessimistic(state, {
          data: payload.note,
          scope: requisitionScope(payload.requisitionId),
          correlationId: payload.correlationId,
        }),
      createRequisitionNoteSuccess: actions.createOnePessimisticSuccess,
      createRequisitionNoteFailed: actions.createOnePessimisticFailed,

      deleteOfferNote: actions.removeOnePessimistic,
      deleteOfferNoteSuccess: actions.removeOnePessimisticSuccess,
      deleteOfferNoteFailed: actions.removeOnePessimisticFailed,

      deleteTalentPoolNote: actions.removeOnePessimistic,
      deleteTalentPoolNoteSuccess: actions.removeOnePessimisticSuccess,
      deleteTalentPoolNoteFailed: actions.removeOnePessimisticFailed,

      deleteCandidateNote: actions.removeOnePessimistic,
      deleteCandidateNoteSuccess: actions.removeOnePessimisticSuccess,
      deleteCandidateNoteFailed: actions.removeOnePessimisticFailed,

      deleteRequisitionNote: actions.removeOnePessimistic,
      deleteRequisitionNoteSuccess: actions.removeOnePessimisticSuccess,
      deleteRequisitionNoteFailed: actions.removeOnePessimisticFailed,

      pin: (state, payload: { id: number }) => state,
      pinSuccess: (state, payload: Note) => state,
      pinFailed: (state, payload: HttpErrorResponse) => state,

      unpin: (state, payload: { id: number }) => state,
      unpinSuccess: (state, payload: Note) => state,
      unpinFailed: (state, payload: HttpErrorResponse) => state,
    };
  },
});

const nestNotes = (flatNotes: Note[]): Note[] => {
  const rootNotes = flatNotes.filter(({ replyToId }) => !replyToId);
  const repliesMap: Record<number, Note[]> = {};
  const getReplies = (id: number): Note[] => {
    const replies = repliesMap[id] || [];
    return [...replies, ...replies.flatMap(({ id }) => getReplies(<number>id))];
  };
  flatNotes.forEach(note => {
    const { replyToId } = note;
    if (!replyToId) return;

    repliesMap[replyToId!] = [...(repliesMap[replyToId!] || []), note];
  });
  return rootNotes.map(note => ({ ...note, replies: getReplies(<number>note.id) }));
};

const selectCandidateScope = memoizeSelector((candidateId: number) =>
  createSelector(
    defaultSelectors.entitiesWithReferences({ scope: candidateScope(candidateId) }),
    (notes: Note[]) => notes,
  ),
);

const selectOfferScope = memoizeSelector((offerId: number) =>
  createSelector(
    defaultSelectors.entitiesWithReferences({ scope: offerScope(offerId) }),
    (notes: Note[]) => notes,
  ),
);

const selectTalentPoolScope = memoizeSelector((offerId: number) =>
  createSelector(
    defaultSelectors.entitiesWithReferences({ scope: talentPoolScope(offerId) }),
    (notes: Note[]) => notes,
  ),
);

const selectRequisitionScope = memoizeSelector((requisitionId: number) =>
  createSelector(
    defaultSelectors.entitiesWithReferences({ scope: requisitionScope(requisitionId) }),
    (notes: Note[]) => notes,
  ),
);

const selectors = {
  ...defaultSelectors,
  candidateScope: selectCandidateScope,
  candidateScopeNested: memoizeSelector((candidateId: number) =>
    createSelector(selectCandidateScope(candidateId), (notes: Note[]) => nestNotes(notes)),
  ),
  candidateScopeMeta: memoizeSelector((candidateId: number) =>
    createSelector(
      defaultSelectors.isLoaded({ scope: candidateScope(candidateId) }),
      defaultSelectors.isPending({ scope: candidateScope(candidateId) }),
      defaultSelectors.isFailed({ scope: candidateScope(candidateId) }),
      defaultSelectors.error({ scope: candidateScope(candidateId) }),
      (isLoaded, isPending, isFailed, error) => ({
        isLoaded,
        isPending,
        isFailed,
        error,
      }),
    ),
  ),
  offerScope: selectOfferScope,
  offerScopeNested: memoizeSelector((offerId: number) =>
    createSelector(selectOfferScope(offerId), (notes: Note[]) => nestNotes(notes)),
  ),
  offerScopeIsLoaded: memoizeSelector((offerId: number) =>
    defaultSelectors.isLoaded({ scope: offerScope(offerId) }),
  ),
  talentPoolScope: selectTalentPoolScope,
  talentPoolScopeNested: memoizeSelector((offerId: number) =>
    createSelector(selectTalentPoolScope(offerId), (notes: Note[]) => nestNotes(notes)),
  ),
  talentPoolScopeIsLoaded: memoizeSelector((offerId: number) =>
    defaultSelectors.isLoaded({ scope: talentPoolScope(offerId) }),
  ),
  requisitionScope: selectRequisitionScope,
  requisitionScopeNested: memoizeSelector((requisitionId: number) =>
    createSelector(selectRequisitionScope(requisitionId), (notes: Note[]) => nestNotes(notes)),
  ),
  requisitionScopeIsLoaded: memoizeSelector((requisitionId: number) =>
    defaultSelectors.isLoaded({ scope: requisitionScope(requisitionId) }),
  ),
};

const NoteActions = <
  Pick<
    typeof actions,
    | 'updateOnePessimistic'
    | 'updateOnePessimisticSuccess'
    | 'updateOnePessimisticFailed'
    | 'deleteOfferNote'
    | 'deleteOfferNoteSuccess'
    | 'deleteOfferNoteFailed'
    | 'deleteTalentPoolNote'
    | 'deleteTalentPoolNoteSuccess'
    | 'deleteTalentPoolNoteFailed'
    | 'deleteCandidateNote'
    | 'deleteCandidateNoteSuccess'
    | 'deleteCandidateNoteFailed'
    | 'fetchCandidateNotes'
    | 'fetchCandidateNotesSuccess'
    | 'fetchCandidateNotesFailed'
    | 'createCandidateNote'
    | 'createCandidateNoteSuccess'
    | 'createCandidateNoteFailed'
    | 'fetchOfferNotes'
    | 'fetchOfferNotesSuccess'
    | 'fetchOfferNotesFailed'
    | 'fetchTalentPoolNotes'
    | 'fetchTalentPoolNotesSuccess'
    | 'fetchTalentPoolNotesFailed'
    | 'createOfferNote'
    | 'createOfferNoteSuccess'
    | 'createOfferNoteFailed'
    | 'createTalentPoolNote'
    | 'createTalentPoolNoteSuccess'
    | 'createTalentPoolNoteFailed'
    | 'fetchRequisitionNotes'
    | 'fetchRequisitionNotesSuccess'
    | 'fetchRequisitionNotesFailed'
    | 'createRequisitionNote'
    | 'createRequisitionNoteSuccess'
    | 'createRequisitionNoteFailed'
    | 'deleteRequisitionNote'
    | 'deleteRequisitionNoteSuccess'
    | 'deleteRequisitionNoteFailed'
    | 'pin'
    | 'pinSuccess'
    | 'pinFailed'
    | 'unpin'
    | 'unpinSuccess'
    | 'unpinFailed'
    | 'updateChecklist'
    | 'updateChecklistSuccess'
    | 'updateChecklistFailed'
  >
>actions;

export { NoteActions, selectors as NoteSelectors, actions, namespace, reducer };
